/**
 * Inline artifact cards for coworker threads: rendered under an assistant
 * message from `message.parameters.coworkerArtifacts` (pseudo-file entries
 * materialized by the backend — see utils/task_conversation_sync.js).
 * Images, videos, audio, and 3D models resolve a short-lived presigned URL
 * and preview inline; other files render as a name row (opens the in-app
 * viewer when we have one — PDFs and PowerPoint on desktop go to the
 * preview sidebar) plus a separate Download control.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Pressable, StyleSheet, ActivityIndicator, Animated, Easing, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useVideoPlayer, VideoView } from 'expo-video';
import { useTheme } from '@/context/ThemeContext';
import { useOnScreen } from '@/hooks/useOnScreen';
import { useLightboxStore, useToastStore } from '@/store';
import { coworkerApi } from '@/lib/api';
import {
  getFileIconByExtension,
  formatFileSize,
  openMessageFile,
  canUseDesktopEditors,
  openFileExternally,
  isPdfFile,
  isPresentationFile,
  isSpreadsheetFile,
  isWordDocumentFile,
  isCsvFile,
  isTsvFile,
  isCodeFile,
} from '@/constants/utils/files';
import { isAudioArtifact, isHtmlArtifact, isModel3DArtifact, isVideoArtifact, isScratchVideoClipName, artifactBaseName, model3DFormatFromArtifact, coworkerArtifactAsFile, presentWorkArtifacts } from '@/components/Coworker/coworkerArtifactTypes';
import HtmlPlayablePreview from '@/components/Coworker/HtmlPlayablePreview';
import WorkAppear from '@/components/Coworker/WorkAppear';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import AudioWaveformPlayer from '@/components/ChatInput/AudioWaveformPlayer';
import Model3DCardContent from '@/components/Model3D/Model3DCardContent';

function formatBytes(bytes) {
  return formatFileSize(bytes || 0);
}

const PREVIEW_RETRY_MS = [0, 800, 1600, 3200, 5000];

// Every presigned GET is minted with a fresh signature, so remounting a card
// (scrolling a long thread) produced a brand-new URL for identical bytes —
// a different cache key, meaning another download and another full decode
// while the previous bitmap was still held. Reuse one URL per artifact, well
// inside the 900s server-side lifetime (see taskRoutes presignArtifactGet).
const PRESIGNED_TTL_MS = 10 * 60 * 1000;
const PRESIGNED_CACHE_MAX = 200;
const presignedCache = new Map();

function presignedCacheKey(taskId, artifactPath, size) {
  return `${taskId}|${artifactPath}|${Number(size) || 0}`;
}

function cachedPresignedUrl(taskId, artifactPath, size) {
  const key = presignedCacheKey(taskId, artifactPath, size);
  const hit = presignedCache.get(key);
  if (hit && hit.expiresAt > Date.now()) return hit.url;
  if (hit) presignedCache.delete(key);
  return null;
}

function rememberPresignedUrl(taskId, artifactPath, url, size) {
  if (presignedCache.size >= PRESIGNED_CACHE_MAX) {
    const oldest = presignedCache.keys().next();
    if (!oldest.done) presignedCache.delete(oldest.value);
  }
  presignedCache.set(presignedCacheKey(taskId, artifactPath, size), {
    url,
    expiresAt: Date.now() + PRESIGNED_TTL_MS,
  });
}

function forgetPresignedUrl(taskId, artifactPath) {
  const prefix = `${taskId}|${artifactPath}|`;
  for (const key of [...presignedCache.keys()]) {
    if (key.startsWith(prefix) || key === `${taskId}|${artifactPath}`) presignedCache.delete(key);
  }
}

async function loadArtifactPreviewBlob(taskId, artifactPath) {
  const presigned = await coworkerApi.getArtifactDownloadUrl(taskId, artifactPath);
  if (presigned) {
    const response = await fetch(presigned);
    if (response.ok) return response.blob();
  }
  const response = await coworkerApi.fetchArtifactBinary(taskId, artifactPath, { asProxy: true });
  if (!response.ok) throw new Error('Download failed');
  return response.blob();
}

async function loadArtifactPresignedSource(taskId, artifactPath, size) {
  const cached = cachedPresignedUrl(taskId, artifactPath, size);
  if (cached) return { kind: 'presigned', url: cached };
  const presigned = await coworkerApi.getArtifactDownloadUrl(taskId, artifactPath);
  if (presigned) {
    rememberPresignedUrl(taskId, artifactPath, presigned, size);
    return { kind: 'presigned', url: presigned };
  }
  return { kind: 'blob', blob: await loadArtifactPreviewBlob(taskId, artifactPath) };
}

async function loadArtifactImageSource(taskId, artifactPath, size) {
  return loadArtifactPresignedSource(taskId, artifactPath, size);
}

async function loadArtifactPlayableSource(taskId, artifactPath, size) {
  return loadArtifactPresignedSource(taskId, artifactPath, size);
}

// Spreading a whole Uint8Array into String.fromCharCode throws a RangeError
// past a few hundred KB of arguments — any real video or deck. Encode in
// slices instead.
function bytesToBase64(bytes) {
  const view = new Uint8Array(bytes);
  const SLICE = 0x8000;
  let binary = '';
  for (let i = 0; i < view.length; i += SLICE) {
    binary += String.fromCharCode.apply(null, view.subarray(i, i + SLICE));
  }
  return btoa(binary);
}

async function fetchArtifactResponse(taskId, artifact) {
  const downloadUrl = await coworkerApi.getArtifactDownloadUrl(taskId, artifact.path);
  let response = null;
  if (downloadUrl) {
    try {
      response = await fetch(downloadUrl);
    } catch {
      response = null;
    }
  }
  if (!response?.ok) {
    response = await coworkerApi.fetchArtifactBinary(taskId, artifact.path, { asProxy: true });
  }
  if (!response.ok) throw new Error('Download failed');
  return response;
}

async function saveArtifactWeb(taskId, artifact) {
  const response = await fetchArtifactResponse(taskId, artifact);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = artifact.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click has been handed to the download manager; doing it
  // synchronously races Safari and drops the download.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Native: stream the presigned GET straight to disk. Buffering the whole
// file in JS (arrayBuffer → base64 string → write) doubled peak memory and
// OOM'd low-end phones on large deliverables; the proxy path only remains
// as a fallback when no presigned URL is available.
async function saveArtifactNative(taskId, artifact) {
  const FileSystem = await import('expo-file-system');
  const safeName = artifact.name.replace(/[^A-Za-z0-9._-]/g, '_');
  const fileUri = `${FileSystem.cacheDirectory}${safeName}`;
  const downloadUrl = await coworkerApi.getArtifactDownloadUrl(taskId, artifact.path).catch(() => null);
  if (downloadUrl) {
    try {
      const result = await FileSystem.downloadAsync(downloadUrl, fileUri,
        Platform.OS === 'macos' ? { sessionType: FileSystem.FileSystemSessionType.FOREGROUND } : {});
      if (result?.status >= 200 && result?.status < 300) return fileUri;
    } catch { /* fall through to the authenticated proxy */ }
  }
  const response = await coworkerApi.fetchArtifactBinary(taskId, artifact.path, { asProxy: true });
  if (!response.ok) throw new Error('Download failed');
  const base64 = bytesToBase64(await response.arrayBuffer());
  await FileSystem.writeAsStringAsync(fileUri, base64, { encoding: FileSystem.EncodingType.Base64 });
  return fileUri;
}

async function shareNativeFile(fileUri, addToast) {
  const Sharing = await import('expo-sharing');
  if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(fileUri);
  else addToast?.('File saved to cache', 'info');
}

async function downloadArtifact(taskId, artifact, { addToast } = {}) {
  if (Platform.OS === 'web') {
    await saveArtifactWeb(taskId, artifact);
    return;
  }
  const fileUri = await saveArtifactNative(taskId, artifact);
  await shareNativeFile(fileUri, addToast);
}

function useArtifactDownload(taskId, artifact) {
  const { addToast } = useToastStore((s) => s.actions);
  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadArtifact(taskId, artifact, { addToast });
    } catch (err) {
      addToast(err?.message || 'Download failed', 'error');
    } finally {
      setIsDownloading(false);
    }
  }, [artifact, taskId, isDownloading, addToast]);

  return { isDownloading, download };
}

// Multi-deliverable tasks used to mean one Download tap per file. Files are
// fetched one after another so N presigned GETs do not race each other on a
// phone radio; on native each lands in the share sheet in turn.
function useDownloadAll(taskId, artifacts) {
  const { addToast } = useToastStore((s) => s.actions);
  const [progress, setProgress] = useState(null);

  const downloadAll = useCallback(async () => {
    if (progress || !artifacts.length) return;
    let failed = 0;
    for (let i = 0; i < artifacts.length; i += 1) {
      setProgress({ done: i, total: artifacts.length });
      try {
        await downloadArtifact(taskId, artifacts[i], { addToast });
      } catch {
        failed += 1;
      }
    }
    setProgress(null);
    if (failed) {
      addToast(`${failed} of ${artifacts.length} files could not be downloaded`, 'error');
    } else if (Platform.OS === 'web') {
      addToast(`Downloaded ${artifacts.length} files`, 'success');
    }
  }, [addToast, artifacts, progress, taskId]);

  return { progress, downloadAll };
}

// Pulsing placeholder in the final card's footprint. A centered spinner
// reads as "nothing here yet"; a block the size of the incoming media reads
// as "loading this", and the swap-in does not shift layout.
function ArtifactShimmer({ color, style }) {
  const opacity = useRef(new Animated.Value(0.45)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 700,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.45,
          duration: 700,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [opacity]);

  return (
    <Animated.View
      pointerEvents="none"
      style={[styles.imageLoading, { backgroundColor: color, opacity }, style]}
    />
  );
}

function ArtifactNativeVideoPlayer({ url }) {
  const player = useVideoPlayer(url, (p) => {
    p.loop = false;
    p.muted = false;
    p.pause();
  });

  useEffect(() => () => {
    try { player.pause(); } catch { /* already released */ }
  }, [player]);

  return (
    <VideoView
      player={player}
      style={styles.videoSurface}
      nativeControls
      allowsFullscreen
      allowsPictureInPicture={false}
      contentFit="contain"
    />
  );
}

function ArtifactWebVideo({ url, name, onError }) {
  const videoRef = useRef(null);
  const framedRef = useRef(false);

  useEffect(() => {
    framedRef.current = false;
  }, [url]);

  const paintFirstFrame = useCallback(() => {
    const el = videoRef.current;
    if (!el || framedRef.current) return;
    const duration = Number(el.duration);
    if (!Number.isFinite(duration) || duration <= 0) return;
    framedRef.current = true;
    try {
      el.currentTime = Math.min(0.1, duration * 0.01);
    } catch { /* some browsers throw if the seek isn't allowed yet */ }
  }, []);

  return (
    // eslint-disable-next-line react/forbid-elements
    <video
      ref={videoRef}
      controls
      playsInline
      preload="metadata"
      src={url}
      onLoadedMetadata={paintFirstFrame}
      onError={onError}
      aria-label={name}
      style={{
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit: 'contain',
        backgroundColor: '#000',
      }}
    />
  );
}

function ArtifactImage({ artifact, taskId, colors, border }) {
  const [hostRef, isOnScreen] = useOnScreen();
  const { openLightbox } = useLightboxStore((s) => s.actions);
  const [url, setUrl] = useState(null);
  const [isFailed, setIsFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const sourceKindRef = useRef(null);
  const blobUrlRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    setUrl((prev) => (prev == null ? prev : null));
    setIsFailed(false);
    setIsLoading(true);
  }, [taskId, artifact.path, artifact.size]);

  useEffect(() => {
    if (!isOnScreen || url) return undefined;
    let cancelled = false;
    aliveRef.current = true;
    (async () => {
      // Settlement lists the file immediately; the CDN/R2 copy (and even
      // the host proxy while status flips to idle) can lag a few seconds.
      // Keep the card in a loading state and retry instead of flashing
      // "preview unavailable".
      for (let i = 0; i < PREVIEW_RETRY_MS.length; i++) {
        if (PREVIEW_RETRY_MS[i]) {
          await new Promise((resolve) => setTimeout(resolve, PREVIEW_RETRY_MS[i]));
        }
        if (cancelled) return;
        try {
          const loaded = await loadArtifactImageSource(taskId, artifact.path, artifact.size);
          if (cancelled) return;
          sourceKindRef.current = loaded.kind;
          if (loaded.kind === 'blob') {
            const objectUrl = URL.createObjectURL(loaded.blob);
            blobUrlRef.current = objectUrl;
            setUrl(objectUrl);
          } else {
            setUrl(loaded.url);
          }
          return;
        } catch {
          if (cancelled) return;
        }
      }
      if (!cancelled) setIsFailed(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOnScreen, url, taskId, artifact.path, artifact.size]);

  useEffect(() => () => {
    aliveRef.current = false;
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, [taskId, artifact.path, artifact.size]);

  const handleImageError = useCallback(async () => {
    if (sourceKindRef.current !== 'presigned') {
      setIsFailed(true);
      setIsLoading(false);
      return;
    }
    forgetPresignedUrl(taskId, artifact.path);
    sourceKindRef.current = 'blob';
    setIsLoading(true);
    try {
      const blob = await loadArtifactPreviewBlob(taskId, artifact.path);
      if (!aliveRef.current) return;
      const objectUrl = URL.createObjectURL(blob);
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = objectUrl;
      setUrl(objectUrl);
    } catch {
      if (aliveRef.current) setIsFailed(true);
    } finally {
      if (aliveRef.current) setIsLoading(false);
    }
  }, [artifact.path, taskId]);

  if (isFailed) {
    return (
      <View style={[styles.row, { borderColor: border }]}>
        <Icon name="file-image" size={14} color={colors.text_color_secondary} />
        <Text size="s" color="secondary" numberOfLines={1} style={{ flex: 1, marginLeft: 8 }}>
          {artifact.name} — preview unavailable
        </Text>
      </View>
    );
  }

  return (
    <View ref={hostRef}>
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Open ${artifact.name}`}
      {...(Platform.OS === 'web' ? { title: artifact.name } : {})}
      style={({ hovered, pressed }) => [
        styles.imageCard,
        {
          borderColor: hovered || pressed ? colors.border_color_primary : border,
          backgroundColor: colors.solid_color_primary,
        },
      ]}
      onPress={() => {
        if (!url) return;
        if (canUseDesktopEditors()) {
          openMessageFile({ ...coworkerArtifactAsFile(artifact, url), fileType: 'image', taskId, path: artifact.path });
        } else {
          openLightbox({ imageUrl: url, dismissOnOutsidePress: true });
        }
      }}
    >
      {isOnScreen && isLoading && (
        <ArtifactShimmer color={colors.solid_color_secondary} style={styles.imageShimmer} />
      )}
      {isOnScreen && url ? (
        <Image
          source={{ uri: url }}
          style={styles.image}
          contentFit="cover"
          cachePolicy="disk"
          transition={150}
          onLoadEnd={() => setIsLoading(false)}
          onError={handleImageError}
        />
      ) : (
        <View style={styles.image} />
      )}
      <View style={styles.imageCaption}>
        <Icon name="file-image" size={10} color={colors.text_color_tertiary} />
        <Text size="xxs" weight="medium" color="secondary" numberOfLines={1} style={{ flex: 1 }}>
          {artifact.name}
        </Text>
        {!!artifact.size && <Text size="xxs" color="tertiary">{formatBytes(artifact.size)}</Text>}
        <Icon name="expand" size={9} color={colors.text_color_tertiary} />
      </View>
    </Pressable>
    </View>
  );
}

function ArtifactVideo({ artifact, taskId, colors, border }) {
  const [hostRef, isOnScreen] = useOnScreen();
  const [url, setUrl] = useState(null);
  const [isFailed, setIsFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const sourceKindRef = useRef(null);
  const blobUrlRef = useRef(null);
  const aliveRef = useRef(true);
  const { isDownloading, download } = useArtifactDownload(taskId, artifact);

  useEffect(() => {
    setUrl((prev) => (prev == null ? prev : null));
    setIsFailed(false);
    setIsLoading(true);
  }, [taskId, artifact.path, artifact.size]);

  useEffect(() => {
    if (!isOnScreen || url) return undefined;
    let cancelled = false;
    aliveRef.current = true;
    (async () => {
      for (let i = 0; i < PREVIEW_RETRY_MS.length; i++) {
        if (PREVIEW_RETRY_MS[i]) {
          await new Promise((resolve) => setTimeout(resolve, PREVIEW_RETRY_MS[i]));
        }
        if (cancelled) return;
        try {
          const loaded = await loadArtifactPlayableSource(taskId, artifact.path, artifact.size);
          if (cancelled) return;
          sourceKindRef.current = loaded.kind;
          if (loaded.kind === 'blob') {
            const objectUrl = URL.createObjectURL(loaded.blob);
            blobUrlRef.current = objectUrl;
            setUrl(objectUrl);
          } else {
            setUrl(loaded.url);
          }
          setIsLoading(false);
          return;
        } catch {
          if (cancelled) return;
        }
      }
      if (!cancelled) {
        setIsFailed(true);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOnScreen, url, taskId, artifact.path, artifact.size]);

  useEffect(() => () => {
    aliveRef.current = false;
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, [taskId, artifact.path, artifact.size]);

  const handlePlaybackError = useCallback(async () => {
    if (sourceKindRef.current !== 'presigned') {
      setIsFailed(true);
      return;
    }
    forgetPresignedUrl(taskId, artifact.path);
    sourceKindRef.current = 'blob';
    setIsLoading(true);
    try {
      const blob = await loadArtifactPreviewBlob(taskId, artifact.path);
      if (!aliveRef.current) return;
      const objectUrl = URL.createObjectURL(blob);
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = objectUrl;
      setUrl(objectUrl);
    } catch {
      if (aliveRef.current) setIsFailed(true);
    } finally {
      if (aliveRef.current) setIsLoading(false);
    }
  }, [artifact.path, taskId]);

  if (isFailed) {
    return <ArtifactFileRow artifact={artifact} taskId={taskId} colors={colors} border={border} />;
  }

  return (
    <View ref={hostRef} style={[styles.videoCard, { borderColor: border, backgroundColor: colors.solid_color_primary }]}>
      <View style={styles.videoFrame}>
        {isOnScreen && isLoading && (
          <ArtifactShimmer color={colors.solid_color_secondary} />
        )}
        {isOnScreen && url ? (
          Platform.OS === 'web' ? (
            <ArtifactWebVideo key={url} url={url} name={artifact.name} onError={handlePlaybackError} />
          ) : (
            <ArtifactNativeVideoPlayer key={url} url={url} />
          )
        ) : null}
      </View>
      <View style={styles.videoCaption}>
        <Icon name={getFileIconByExtension((artifact.name.split('.').pop() || '').toLowerCase()) || 'film'} size={10} color={colors.text_color_tertiary} />
        <View style={styles.fileCopy}>
          <Text size="xxs" weight="medium" color="secondary" numberOfLines={1}>
            {artifact.name}
          </Text>
          <Text size="xxs" color="tertiary">
            {[artifact.name.split('.').pop()?.toUpperCase(), artifact.size ? formatBytes(artifact.size) : null].filter(Boolean).join(' · ')}
          </Text>
        </View>
        {Platform.OS === 'web' && (
          <Pressable
            onPress={() => {
              const file = coworkerArtifactAsFile(artifact, url || '');
              file.path = artifact.path;
              file.source = 'inference';
              file.fileType = 'video';
              openMessageFile(file, { taskId });
            }}
            accessibilityRole="button"
            accessibilityLabel={`Open ${artifact.name}`}
            style={({ hovered, pressed }) => [
              styles.downloadAction,
              (hovered || pressed) && { opacity: 0.75 },
            ]}
          >
            <Text size="xxs" color="secondary" weight="medium">Open</Text>
            <Icon name="expand" size={11} color={colors.text_color_secondary} />
          </Pressable>
        )}
        <Pressable
          onPress={download}
          disabled={isDownloading}
          accessibilityRole="button"
          accessibilityLabel={`Download ${artifact.name}`}
          style={({ hovered, pressed }) => [
            styles.downloadAction,
            (hovered || pressed) && { opacity: 0.75 },
          ]}
        >
          {isDownloading ? (
            <ActivityIndicator size="small" color={colors.text_color_secondary} />
          ) : (
            <>
              <Text size="xxs" color="secondary" weight="medium">Download</Text>
              <Icon name="arrow-down-to-line" size={11} color={colors.text_color_secondary} />
            </>
          )}
        </Pressable>
      </View>
    </View>
  );
}

function ArtifactHtml({ artifact, taskId }) {
  const loadHtml = useCallback(async () => {
    const downloadUrl = await coworkerApi.getArtifactDownloadUrl(taskId, artifact.path);
    if (downloadUrl) {
      try {
        const response = await fetch(downloadUrl);
        if (response.ok) return response.text();
      } catch { /* fall through to the authenticated proxy */ }
    }
    const response = await coworkerApi.fetchArtifactBinary(taskId, artifact.path, { asProxy: true });
    if (!response.ok) throw new Error('Download failed');
    return response.text();
  }, [artifact.path, taskId]);

  const expand = useCallback(async () => {
    const url = await coworkerApi.getArtifactDownloadUrl(taskId, artifact.path).catch(() => null);
    const file = coworkerArtifactAsFile(artifact, url || '');
    openMessageFile(file);
  }, [artifact, taskId]);

  return (
    <HtmlPlayablePreview
      name={artifact.name || artifact.path}
      sourceKey={`${taskId}|${artifact.path}|${artifact.size || 0}`}
      loadHtml={loadHtml}
      onExpand={expand}
    />
  );
}

function ArtifactFileRow({ artifact, taskId, colors, border }) {
  const { addToast } = useToastStore((s) => s.actions);
  const { isDownloading, download } = useArtifactDownload(taskId, artifact);
  const [isOpening, setIsOpening] = useState(false);
  const icon = getFileIconByExtension((artifact.name.split('.').pop() || '').toLowerCase()) || 'file-lines';

  const open = useCallback(async () => {
    if (isOpening || isDownloading) return;
    setIsOpening(true);
    try {
      const loaded = await loadArtifactPresignedSource(taskId, artifact.path, artifact.size);
      if (loaded.kind !== 'presigned' || !loaded.url) {
        addToast('Preview unavailable — downloading instead', 'info');
        await download();
        return;
      }
      const file = coworkerArtifactAsFile(artifact, loaded.url);
      // Text/source artifacts (including Markdown) have a dependable in-app
      // preview. Sending them through window.open is both less polished and
      // can be silently blocked as a popup after the presign request awaits.
      if (
        isPdfFile(file)
        || isPresentationFile(file)
        || isSpreadsheetFile(file)
        || isWordDocumentFile(file)
        || isCsvFile(file)
        || isTsvFile(file)
        || isCodeFile(file)
      ) openMessageFile(file);
      else openFileExternally(file);
    } catch (err) {
      addToast(err?.message || 'Could not open file', 'error');
    } finally {
      setIsOpening(false);
    }
  }, [addToast, artifact, download, isDownloading, isOpening, taskId]);

  return (
    <View
      style={[
        styles.row,
        { borderColor: border, backgroundColor: colors.solid_color_primary },
      ]}
    >
      <Pressable
        onPress={open}
        disabled={isOpening || isDownloading}
        accessibilityRole="button"
        accessibilityLabel={`Open ${artifact.name}`}
        {...(Platform.OS === 'web' ? { title: artifact.name } : {})}
        style={({ hovered, pressed }) => [
          styles.fileOpen,
          (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
        ]}
      >
        <View style={[styles.fileIcon, { backgroundColor: colors.solid_color_secondary }]}>
          <Icon name={icon} size={15} color={colors.text_color_secondary} />
        </View>
        <View style={styles.fileCopy}>
          <Text
            size="s"
            weight="medium"
            numberOfLines={2}
            ellipsizeMode="tail"
            style={styles.fileName}
          >
            {artifact.name}
          </Text>
          <Text size="xxs" color="tertiary">
            {[artifact.name.split('.').pop()?.toUpperCase(), artifact.size ? formatBytes(artifact.size) : null].filter(Boolean).join(' · ')}
          </Text>
        </View>
        {isOpening ? (
          <ActivityIndicator size="small" color={colors.text_color_secondary} />
        ) : null}
      </Pressable>
      <Pressable
        onPress={download}
        disabled={isDownloading || isOpening}
        accessibilityRole="button"
        accessibilityLabel={`Download ${artifact.name}`}
        style={({ hovered, pressed }) => [
          styles.downloadAction,
          (hovered || pressed) && { opacity: 0.75 },
        ]}
      >
        {isDownloading ? (
          <ActivityIndicator size="small" color={colors.text_color_secondary} />
        ) : (
          <>
            <Text size="xxs" color="secondary" weight="medium">Download</Text>
            <Icon name="arrow-down-to-line" size={11} color={colors.text_color_secondary} />
          </>
        )}
      </Pressable>
    </View>
  );
}

function ArtifactAudio({ artifact, taskId, colors, border }) {
  const [hostRef, isOnScreen] = useOnScreen();
  const [url, setUrl] = useState(null);
  const [isFailed, setIsFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const blobUrlRef = useRef(null);
  const { isDownloading, download } = useArtifactDownload(taskId, artifact);
  const playerFile = useMemo(() => coworkerArtifactAsFile({
    ...artifact,
    fileType: artifact?.fileType || 'audio',
  }, url), [artifact, url]);

  useEffect(() => {
    setUrl((prev) => (prev == null ? prev : null));
    setIsFailed(false);
    setIsLoading(true);
  }, [taskId, artifact.path, artifact.size]);

  useEffect(() => {
    if (!isOnScreen || url) return undefined;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < PREVIEW_RETRY_MS.length; i++) {
        if (PREVIEW_RETRY_MS[i]) {
          await new Promise((resolve) => setTimeout(resolve, PREVIEW_RETRY_MS[i]));
        }
        if (cancelled) return;
        try {
          const loaded = await loadArtifactPlayableSource(taskId, artifact.path, artifact.size);
          if (cancelled) return;
          if (loaded.kind === 'blob') {
            const objectUrl = URL.createObjectURL(loaded.blob);
            blobUrlRef.current = objectUrl;
            setUrl(objectUrl);
          } else {
            setUrl(loaded.url);
          }
          setIsLoading(false);
          return;
        } catch {
          if (cancelled) return;
        }
      }
      if (!cancelled) {
        setIsFailed(true);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOnScreen, url, taskId, artifact.path, artifact.size]);

  useEffect(() => () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, [taskId, artifact.path, artifact.size]);

  if (isFailed) {
    return <ArtifactFileRow artifact={artifact} taskId={taskId} colors={colors} border={border} />;
  }

  return (
    <View ref={hostRef} style={[styles.videoCard, { borderColor: border, backgroundColor: colors.solid_color_primary }]}>
      <View style={styles.audioFrame}>
        {isOnScreen && isLoading && (
          <ArtifactShimmer color={colors.solid_color_secondary} />
        )}
        {isOnScreen && url ? (
          <AudioWaveformPlayer
            sourceUrl={url}
            size={520}
            file={playerFile}
          />
        ) : null}
      </View>
      <View style={styles.videoCaption}>
        <Icon name="music" size={10} color={colors.text_color_tertiary} />
        <View style={styles.fileCopy}>
          <Text size="xxs" weight="medium" color="secondary" numberOfLines={1}>
            {artifact.name}
          </Text>
          <Text size="xxs" color="tertiary">
            {[artifact.name.split('.').pop()?.toUpperCase(), artifact.size ? formatBytes(artifact.size) : null].filter(Boolean).join(' · ')}
          </Text>
        </View>
        <Pressable
          onPress={download}
          disabled={isDownloading}
          accessibilityRole="button"
          accessibilityLabel={`Download ${artifact.name}`}
          style={({ hovered, pressed }) => [
            styles.downloadAction,
            (hovered || pressed) && { opacity: 0.75 },
          ]}
        >
          {isDownloading ? (
            <ActivityIndicator size="small" color={colors.text_color_secondary} />
          ) : (
            <>
              <Text size="xxs" color="secondary" weight="medium">Download</Text>
              <Icon name="arrow-down-to-line" size={11} color={colors.text_color_secondary} />
            </>
          )}
        </Pressable>
      </View>
    </View>
  );
}

function ArtifactModel3D({ artifact, taskId, colors, border }) {
  const [hostRef, isOnScreen] = useOnScreen();
  const [url, setUrl] = useState(null);
  const [isFailed, setIsFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { isDownloading, download } = useArtifactDownload(taskId, artifact);
  const format = model3DFormatFromArtifact(artifact);
  const viewerFile = useMemo(() => ({
    name: artifact.name,
    fileType: '3d',
    url,
    cdnPath: url,
    metadata: { '3d': { format } },
  }), [artifact.name, url, format]);

  useEffect(() => {
    setUrl((prev) => (prev == null ? prev : null));
    setIsFailed(false);
    setIsLoading(true);
  }, [taskId, artifact.path, artifact.size]);

  useEffect(() => {
    if (!isOnScreen || url) return undefined;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < PREVIEW_RETRY_MS.length; i++) {
        if (PREVIEW_RETRY_MS[i]) {
          await new Promise((resolve) => setTimeout(resolve, PREVIEW_RETRY_MS[i]));
        }
        if (cancelled) return;
        try {
          const loaded = await loadArtifactPresignedSource(taskId, artifact.path, artifact.size);
          if (cancelled) return;
          // model-viewer infers format from the URL path. Blob object URLs
          // have no extension, so skip that fallback and keep the download row.
          if (loaded.kind !== 'presigned' || !loaded.url) throw new Error('no presigned url');
          setUrl(loaded.url);
          setIsLoading(false);
          return;
        } catch {
          if (cancelled) return;
        }
      }
      if (!cancelled) {
        setIsFailed(true);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOnScreen, url, taskId, artifact.path, artifact.size]);

  if (isFailed) {
    return <ArtifactFileRow artifact={artifact} taskId={taskId} colors={colors} border={border} />;
  }

  return (
    <View ref={hostRef} style={[styles.modelCard, { borderColor: border, backgroundColor: colors.solid_color_primary }]}>
      <View style={styles.modelFrame}>
        {isOnScreen && isLoading && (
          <ArtifactShimmer color={colors.solid_color_secondary} />
        )}
        {isOnScreen && url ? (
          <Model3DCardContent file={viewerFile} autoActivate viewerOnly={false} />
        ) : null}
      </View>
      <View style={styles.videoCaption}>
        <Icon name="cube" size={10} color={colors.text_color_tertiary} />
        <View style={styles.fileCopy}>
          <Text size="xxs" weight="medium" color="secondary" numberOfLines={1}>
            {artifact.name}
          </Text>
          <Text size="xxs" color="tertiary">
            {[format.toUpperCase(), artifact.size ? formatBytes(artifact.size) : null].filter(Boolean).join(' · ')}
          </Text>
        </View>
        <Pressable
          onPress={download}
          disabled={isDownloading}
          accessibilityRole="button"
          accessibilityLabel={`Download ${artifact.name}`}
          style={({ hovered, pressed }) => [
            styles.downloadAction,
            (hovered || pressed) && { opacity: 0.75 },
          ]}
        >
          {isDownloading ? (
            <ActivityIndicator size="small" color={colors.text_color_secondary} />
          ) : (
            <>
              <Text size="xxs" color="secondary" weight="medium">Download</Text>
              <Icon name="arrow-down-to-line" size={11} color={colors.text_color_secondary} />
            </>
          )}
        </Pressable>
      </View>
    </View>
  );
}

const NO_ARTIFACTS = [];

export default function CoworkerArtifacts({ taskId, artifacts }) {
  const { colors } = useTheme();
  const border = colors.border_color_secondary;

  const uniqueArtifacts = useMemo(() => {
    if (!Array.isArray(artifacts) || artifacts.length === 0) return NO_ARTIFACTS;
    const seenNames = new Map();
    for (const a of presentWorkArtifacts(artifacts)) {
      const name = String(a?.name || a?.path || '').split('/').pop().toLowerCase();
      if (!name) continue;
      seenNames.set(name, a);
    }
    return [...seenNames.values()];
  }, [artifacts]);
  const { progress: downloadAllProgress, downloadAll } = useDownloadAll(taskId, uniqueArtifacts);

  if (!taskId || uniqueArtifacts.length === 0) return null;

  const images = uniqueArtifacts.filter((a) => a.fileType === 'image' && !isVideoArtifact(a));
  const allVideos = uniqueArtifacts.filter((a) => isVideoArtifact(a));
  const finalVideos = allVideos
    .filter((a) => !isScratchVideoClipName(artifactBaseName(a)))
    .slice()
    .sort((a, b) => (Number(b.size) || 0) - (Number(a.size) || 0));
  const scratchClips = allVideos
    .filter((a) => isScratchVideoClipName(artifactBaseName(a)))
    .slice()
    .sort((a, b) => artifactBaseName(a).localeCompare(artifactBaseName(b), undefined, { numeric: true }));
  // When final stitched videos are present, show those as primary deliverables.
  // Otherwise show the available clips so progress is visible during generation.
  const videos = finalVideos.length > 0 ? finalVideos : scratchClips;
  const models = uniqueArtifacts.filter((a) => !isVideoArtifact(a) && isModel3DArtifact(a));
  const audios = uniqueArtifacts.filter((a) => !isVideoArtifact(a) && !isModel3DArtifact(a) && isAudioArtifact(a));
  const htmlGames = uniqueArtifacts.filter((a) => isHtmlArtifact(a));
  const files = uniqueArtifacts.filter((a) => (
    a.fileType !== 'image'
    && !isVideoArtifact(a)
    && !isModel3DArtifact(a)
    && !isAudioArtifact(a)
    && !isHtmlArtifact(a)
  ));

  return (
    // Deliverables attach after the reply (backend sync, then the CDN copy) —
    // easing the block in reads as "the files just landed" instead of a
    // layout pop under an answer the user is already reading.
    <WorkAppear style={styles.container} duration={220} distance={6}>
      <View style={styles.heading}>
        <View style={[styles.headingIcon, { backgroundColor: colors.solid_color_secondary }]}>
          <Icon name="folder-open" size={12} color={colors.text_color_secondary} />
        </View>
        <View style={styles.headingCopy}>
          <Text size="xs" weight="semibold">Deliverables</Text>
          <Text size="xxs" color="tertiary">
            {`${uniqueArtifacts.length} ${uniqueArtifacts.length === 1 ? 'file' : 'files'} created by Work`}
          </Text>
        </View>
        {uniqueArtifacts.length > 1 && (
          <Pressable
            onPress={downloadAll}
            disabled={!!downloadAllProgress}
            accessibilityRole="button"
            accessibilityLabel={`Download all ${uniqueArtifacts.length} files`}
            style={({ hovered, pressed }) => [
              styles.downloadAllAction,
              { borderColor: border, backgroundColor: colors.solid_color_primary },
              (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
            ]}
          >
            {downloadAllProgress ? (
              <>
                <ActivityIndicator size="small" color={colors.text_color_secondary} />
                <Text size="xxs" color="secondary" weight="medium">
                  {`${downloadAllProgress.done + 1} of ${downloadAllProgress.total}`}
                </Text>
              </>
            ) : (
              <>
                <Icon name="arrow-down-to-line" size={11} color={colors.text_color_secondary} />
                <Text size="xxs" color="secondary" weight="medium">Download all</Text>
              </>
            )}
          </Pressable>
        )}
      </View>
      {images.length > 0 && (
        <View style={styles.imageGrid}>
          {images.map((a) => (
            <ArtifactImage key={a.path} artifact={a} taskId={taskId} colors={colors} border={border} />
          ))}
        </View>
      )}
      {videos.map((a) => (
        <ArtifactVideo key={a.path} artifact={a} taskId={taskId} colors={colors} border={border} />
      ))}
      {finalVideos.length > 0 && scratchClips.length > 0 && (
        <View style={{ marginTop: 2, marginBottom: 2 }}>
          <Text size="xxs" color="tertiary">
            {`Includes ${scratchClips.length} underlying clip segments`}
          </Text>
        </View>
      )}
      {models.map((a) => (
        <ArtifactModel3D key={a.path} artifact={a} taskId={taskId} colors={colors} border={border} />
      ))}
      {audios.map((a) => (
        <ArtifactAudio key={a.path} artifact={a} taskId={taskId} colors={colors} border={border} />
      ))}
      {htmlGames.map((a) => (
        <ArtifactHtml key={a.path} artifact={a} taskId={taskId} />
      ))}
      {files.map((a) => (
        <ArtifactFileRow key={a.path} artifact={a} taskId={taskId} colors={colors} border={border} />
      ))}
    </WorkAppear>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
    marginTop: 10,
    maxWidth: 520,
  },
  heading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 1,
  },
  headingIcon: {
    width: 30,
    height: 30,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headingCopy: {
    flex: 1,
  },
  downloadAllAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    height: 28,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'background-color 140ms ease' } }),
  },
  imageGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  imageCard: {
    borderRadius: 10,
    borderWidth: 1,
    overflow: 'hidden',
    width: 180,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'border-color 140ms ease' } }),
  },
  image: {
    width: '100%',
    height: 124,
  },
  imageLoading: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageShimmer: {
    bottom: undefined,
    height: 124,
  },
  imageCaption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 7,
    flexShrink: 0,
  },
  videoCard: {
    borderRadius: 10,
    borderWidth: 1,
    overflow: 'hidden',
  },
  videoFrame: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#000',
  },
  audioFrame: {
    width: '100%',
    minHeight: 108,
    paddingHorizontal: 8,
    paddingVertical: 10,
    backgroundColor: 'transparent',
  },
  videoSurface: {
    width: '100%',
    height: '100%',
  },
  videoCaption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    flexShrink: 0,
  },
  modelCard: {
    borderRadius: 10,
    borderWidth: 1,
    overflow: 'hidden',
  },
  modelFrame: {
    width: '100%',
    aspectRatio: 1,
    backgroundColor: '#14181c',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 10,
    padding: 9,
    gap: 9,
    ...Platform.select({ web: { transition: 'border-color 140ms ease, background-color 140ms ease' } }),
  },
  fileOpen: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  fileIcon: {
    width: 34,
    height: 34,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileCopy: {
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    maxWidth: '100%',
    ...(Platform.OS === 'web' ? {
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    } : {}),
  },
  downloadAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
});
